import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Modal } from 'antd';
import styles from './ScheduleCalendar.module.scss';
import { PerformerItem } from './components/PerformerItem/PerformerItem';
import { IntervalAssignmentPanel } from './components/IntervalAssignmentPanel/IntervalAssignmentPanel';
import { ScheduleAddressesSummary } from './components/ScheduleAddressesSummary/ScheduleAddressesSummary';
import {
  EmptyScheduleTask,
  ScheduleTask,
} from './components/ScheduleTask/ScheduleTask';
import { ScheduleCalendarView } from './components/ScheduleCalendarView/ScheduleCalendarView';
import {
  defaultIntervalFrom,
  defaultIntervalTo,
  defaultIntervalWeekdays,
  intervalAssignmentPristineDraftModes,
  intervalDateFormat,
  intervalRepeatModes,
} from './constants';
import {
  getIntervalAssignmentControls,
  getIntervalAssignmentSettingsKey,
  getPeriodDays,
  getSchedulePeriodText,
  getScheduleStartMonth,
  getScheduleWorkIntervalCount,
  getScheduleWorkIntervalDateKeys,
  getWorkerWorkIntervalDateKeys,
} from './utils/scheduleCalendarUtils';

export const ScheduleCalendar = ({
  companyId,
  schedules,
  enableIntervalAssignment = false,
  canSaveIntervals = false,
  onAssignIntervals,
  onBackIntervals,
  onCancelIntervals,
  onCompactModeEnable,
  onSaveIntervals,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [isCompactCalendar, setIsCompactCalendar] = useState(false);
  const [isMassAssignmentEnabled, setIsMassAssignmentEnabled] = useState(true);
  const [intervalFrom, setIntervalFrom] = useState(defaultIntervalFrom);
  const [intervalTo, setIntervalTo] = useState(defaultIntervalTo);
  const [repeatMode, setRepeatMode] = useState(intervalRepeatModes.manual);
  const [selectedWeekdays, setSelectedWeekdays] = useState(
    defaultIntervalWeekdays
  );
  const [activePerformerId, setActivePerformerId] = useState(null);
  const [activeIntervalCellKey, setActiveIntervalCellKey] = useState(null);
  const [editingInterval, setEditingInterval] = useState(null);
  const [
    lastAppliedIntervalAssignmentKey,
    setLastAppliedIntervalAssignmentKey,
  ] = useState(null);
  const schedulesToShow = schedules || [];
  const selectedSchedule = schedulesToShow[0] ?? null;
  const selectedScheduleId = selectedSchedule?.id;
  const performers = selectedSchedule?.performers ?? [];
  const periodDays = useMemo(
    () => getPeriodDays(selectedSchedule),
    [selectedSchedule]
  );
  const targetDates = useMemo(() => {
    const selectedWeekdaySet = new Set(selectedWeekdays);

    return periodDays
      .filter((date) => selectedWeekdaySet.has(date.isoWeekday()))
      .map((date) => date.format('YYYY-MM-DD'));
  }, [periodDays, selectedWeekdays]);
  const periodDateKeys = useMemo(
    () => periodDays.map((date) => date.format('YYYY-MM-DD')),
    [periodDays]
  );
  const isManualIntervalAssignment = repeatMode === intervalRepeatModes.manual;
  const workIntervalCount = useMemo(
    () => getScheduleWorkIntervalCount(selectedSchedule),
    [selectedSchedule]
  );
  const workIntervalDateKeys = useMemo(
    () => getScheduleWorkIntervalDateKeys(selectedSchedule),
    [selectedSchedule]
  );
  const activePerformerIntervalDateKeys = useMemo(
    () => getWorkerWorkIntervalDateKeys(selectedSchedule, activePerformerId),
    [activePerformerId, selectedSchedule]
  );
  const intervalAssignmentSettingsKey = useMemo(
    () =>
      getIntervalAssignmentSettingsKey({
        from: intervalFrom,
        repeatMode,
        to: intervalTo,
        weekdays: selectedWeekdays,
      }),
    [intervalFrom, intervalTo, repeatMode, selectedWeekdays]
  );
  const isIntervalAssignmentChanged =
    !lastAppliedIntervalAssignmentKey ||
    intervalAssignmentSettingsKey !== lastAppliedIntervalAssignmentKey;

  const initialSheet = useMemo(() => {
    const selectedScheduleStart = getScheduleStartMonth(selectedSchedule);

    return selectedScheduleStart || moment();
  }, [selectedSchedule]);
  const selectedScheduleName =
    selectedSchedule?.departmentName ?? 'График не выбран';
  const selectedSchedulePeriodText = getSchedulePeriodText(selectedSchedule);
  const canUseIntervalAssignment =
    enableIntervalAssignment &&
    Boolean(selectedSchedule) &&
    performers.length > 0 &&
    typeof onAssignIntervals === 'function';
  const canAssignIntervals = canUseIntervalAssignment && targetDates.length > 0;
  const canAssignIntervalToActivePerformer =
    canAssignIntervals && Boolean(activePerformerId);
  const canAssignManualInterval = canUseIntervalAssignment;
  const canClearIntervals =
    canUseIntervalAssignment &&
    (isMassAssignmentEnabled
      ? workIntervalCount > 0
      : activePerformerIntervalDateKeys.length > 0);

  const filteredPerformers = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    if (!normalizedSearch) {
      return performers;
    }

    return performers.filter((performer) => {
      const searchableValue = [
        performer.fullName,
        performer.speciality,
        performer.specialityName,
        ...(performer.specializations || []),
      ]
        .join(' ')
        .toLowerCase();

      return searchableValue.includes(normalizedSearch);
    });
  }, [performers, searchValue]);

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleCompactCalendarToggle = useCallback(() => {
    setIsCompactCalendar((prevValue) => {
      const nextValue = !prevValue;

      return nextValue;
    });
  }, []);

  const handleWeekdayChange = useCallback((weekday) => {
    setSelectedWeekdays((prevWeekdays) => {
      if (prevWeekdays.includes(weekday)) {
        return prevWeekdays.filter((value) => value !== weekday);
      }

      return [...prevWeekdays, weekday].sort((first, second) => first - second);
    });
  }, []);

  const getIntervalAssignmentPayload = useCallback(
    (
      workerIds,
      dates = targetDates,
      replaceDates = periodDateKeys,
      interval = {}
    ) => {
      const nextIntervalFrom =
        interval.from || intervalFrom || defaultIntervalFrom;
      const nextIntervalTo = interval.to || intervalTo || defaultIntervalTo;

      if (
        !canUseIntervalAssignment ||
        !workerIds.length ||
        (!dates.length && !replaceDates.length)
      ) {
        return null;
      }

      return {
        scheduleId: selectedSchedule.id,
        workerIds,
        dates,
        replaceDates,
        interval: {
          from: nextIntervalFrom,
          to: nextIntervalTo,
        },
      };
    },
    [
      canUseIntervalAssignment,
      intervalFrom,
      intervalTo,
      periodDateKeys,
      selectedSchedule,
      targetDates,
    ]
  );

  const handleAssignIntervalsToAll = useCallback(() => {
    const payload = getIntervalAssignmentPayload(
      performers.map((item) => item.id)
    );

    if (!payload) {
      return;
    }

    setActivePerformerId(null);
    onAssignIntervals(payload);
    setLastAppliedIntervalAssignmentKey(intervalAssignmentSettingsKey);
  }, [
    getIntervalAssignmentPayload,
    intervalAssignmentSettingsKey,
    onAssignIntervals,
    performers,
  ]);

  const handleApplyIntervalToActivePerformer = useCallback(() => {
    const payload = getIntervalAssignmentPayload([activePerformerId]);

    if (!payload) {
      return;
    }

    onAssignIntervals(payload);
    setLastAppliedIntervalAssignmentKey(intervalAssignmentSettingsKey);
  }, [
    activePerformerId,
    getIntervalAssignmentPayload,
    intervalAssignmentSettingsKey,
    onAssignIntervals,
  ]);

  const handleClearAllIntervals = useCallback(() => {
    if (!canClearIntervals) {
      return;
    }

    const targetWorkerIds = isMassAssignmentEnabled
      ? performers.map((item) => item.id)
      : [activePerformerId];
    const targetDateKeys = isMassAssignmentEnabled
      ? workIntervalDateKeys
      : activePerformerIntervalDateKeys;
    const confirmContent = isMassAssignmentEnabled
      ? 'Все назначенные интервалы у исполнителей будут удалены.'
      : 'Все назначенные интервалы выбранного исполнителя будут удалены.';

    Modal.confirm({
      title: 'Очистить все интервалы?',
      content: confirmContent,
      okText: 'Очистить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: () => {
        const payload = getIntervalAssignmentPayload(
          targetWorkerIds,
          [],
          targetDateKeys
        );

        if (!payload) {
          return;
        }

        setActivePerformerId(null);
        setLastAppliedIntervalAssignmentKey(null);
        onAssignIntervals(payload);
      },
    });
  }, [
    activePerformerId,
    activePerformerIntervalDateKeys,
    canClearIntervals,
    getIntervalAssignmentPayload,
    isMassAssignmentEnabled,
    onAssignIntervals,
    performers,
    workIntervalDateKeys,
  ]);

  const handleAssignIntervalToCell = useCallback(
    ({ resource, day }) => {
      if (
        !isManualIntervalAssignment ||
        !canAssignManualInterval ||
        !resource?.id ||
        !day
      ) {
        return;
      }

      const date = moment.isMoment(day)
        ? day.format(intervalDateFormat)
        : moment(day).format(intervalDateFormat);
      const payload = getIntervalAssignmentPayload(
        [resource.id],
        [date],
        [date]
      );

      if (!payload) {
        return;
      }

      setActivePerformerId(resource.id);
      onAssignIntervals(payload);
    },
    [
      canAssignManualInterval,
      getIntervalAssignmentPayload,
      isManualIntervalAssignment,
      onAssignIntervals,
    ]
  );

  const handleClearIntervalCell = useCallback(
    ({ resource, day }) => {
      if (
        !isManualIntervalAssignment ||
        !canAssignManualInterval ||
        !resource?.id ||
        !day
      ) {
        return;
      }

      const parsedDay = moment.isMoment(day) ? day : moment(day);
      const date = parsedDay.format(intervalDateFormat);
      const payload = getIntervalAssignmentPayload([resource.id], [], [date]);

      if (!payload) {
        return;
      }

      setActiveIntervalCellKey(null);
      setEditingInterval(null);
      setActivePerformerId(resource.id);
      onAssignIntervals(payload);
    },
    [
      canAssignManualInterval,
      getIntervalAssignmentPayload,
      isManualIntervalAssignment,
      onAssignIntervals,
    ]
  );

  const getManualIntervalCellProps = useCallback(
    (resource, day) => {
      if (!isManualIntervalAssignment || !canAssignManualInterval) {
        return {};
      }

      const handleCellAction = () =>
        handleAssignIntervalToCell({ resource, day });

      return {
        'aria-label': 'Назначить интервал',
        className: styles.intervalAssignmentCellInteractive,
        role: 'button',
        tabIndex: 0,
        onClick: handleCellAction,
        onKeyDown: (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') {
            return;
          }

          event.preventDefault();
          handleCellAction();
        },
      };
    },
    [
      canAssignManualInterval,
      handleAssignIntervalToCell,
      isManualIntervalAssignment,
    ]
  );

  const handleSelectPerformer = useCallback(
    (performer) => {
      if (isMassAssignmentEnabled || isManualIntervalAssignment) {
        return;
      }

      const intervalControls = getIntervalAssignmentControls(
        selectedSchedule,
        performer.id
      );

      setIntervalFrom(intervalControls?.from || defaultIntervalFrom);
      setIntervalTo(intervalControls?.to || defaultIntervalTo);
      const nextWeekdays = intervalControls?.weekdays?.length
        ? intervalControls.weekdays
        : defaultIntervalWeekdays;

      setSelectedWeekdays(nextWeekdays);

      if (
        intervalControls ||
        intervalAssignmentPristineDraftModes.has(selectedSchedule?._draftMode)
      ) {
        setLastAppliedIntervalAssignmentKey(
          getIntervalAssignmentSettingsKey({
            from: intervalControls?.from || defaultIntervalFrom,
            repeatMode,
            to: intervalControls?.to || defaultIntervalTo,
            weekdays: nextWeekdays,
          })
        );
      } else {
        setLastAppliedIntervalAssignmentKey(null);
      }

      setActivePerformerId(performer.id);
    },
    [
      isManualIntervalAssignment,
      isMassAssignmentEnabled,
      repeatMode,
      selectedSchedule,
    ]
  );

  const renderPerformer = useCallback(
    (performer) => (
      <PerformerItem
        performer={performer}
        component="div"
        fluid
        compact={isCompactCalendar}
        isActive={
          enableIntervalAssignment &&
          !isMassAssignmentEnabled &&
          !isManualIntervalAssignment &&
          String(activePerformerId) === String(performer.id)
        }
        isInteractive={
          enableIntervalAssignment &&
          !isMassAssignmentEnabled &&
          !isManualIntervalAssignment
        }
        onClick={() => handleSelectPerformer(performer)}
      />
    ),
    [
      activePerformerId,
      enableIntervalAssignment,
      handleSelectPerformer,
      isCompactCalendar,
      isManualIntervalAssignment,
      isMassAssignmentEnabled,
    ]
  );

  const renderScheduleTask = useCallback(
    ({ task, resource, day }) => (
      <ScheduleTask
        key={
          task.id ||
          `${resource?.id ?? 'resource'}-${
            moment.isMoment(day) ? day.format(intervalDateFormat) : day
          }`
        }
        activeIntervalCellKey={activeIntervalCellKey}
        canAssignManualInterval={canAssignManualInterval}
        day={day}
        editingInterval={editingInterval}
        getIntervalAssignmentPayload={getIntervalAssignmentPayload}
        isCompactCalendar={isCompactCalendar}
        isManualIntervalAssignment={isManualIntervalAssignment}
        resource={resource}
        task={task}
        onAssignIntervals={onAssignIntervals}
        onClearIntervalCell={handleClearIntervalCell}
        onEditingIntervalChange={setEditingInterval}
        onIntervalCellKeyChange={setActiveIntervalCellKey}
        onPerformerActivate={setActivePerformerId}
      />
    ),
    [
      activeIntervalCellKey,
      canAssignManualInterval,
      editingInterval,
      getIntervalAssignmentPayload,
      handleClearIntervalCell,
      isCompactCalendar,
      isManualIntervalAssignment,
      onAssignIntervals,
    ]
  );

  const renderEmptyScheduleTask = useCallback(
    ({ resource, day }) => (
      <EmptyScheduleTask
        day={day}
        getManualIntervalCellProps={getManualIntervalCellProps}
        isCompactCalendar={isCompactCalendar}
        resource={resource}
      />
    ),
    [getManualIntervalCellProps, isCompactCalendar]
  );

  const renderIntervalAssignment = useCallback(
    () =>
      enableIntervalAssignment ? (
        <IntervalAssignmentPanel
          canAssignIntervalToActivePerformer={
            canAssignIntervalToActivePerformer
          }
          canAssignIntervals={canAssignIntervals}
          canClearIntervals={canClearIntervals}
          canSaveIntervals={canSaveIntervals}
          intervalFrom={intervalFrom}
          intervalTo={intervalTo}
          isIntervalAssignmentChanged={isIntervalAssignmentChanged}
          isManualIntervalAssignment={isManualIntervalAssignment}
          isMassAssignmentEnabled={isMassAssignmentEnabled}
          repeatMode={repeatMode}
          selectedWeekdays={selectedWeekdays}
          onApplyIntervalToActivePerformer={
            handleApplyIntervalToActivePerformer
          }
          onAssignIntervalsToAll={handleAssignIntervalsToAll}
          onBackIntervals={onBackIntervals}
          onCancelIntervals={onCancelIntervals}
          onClearAllIntervals={handleClearAllIntervals}
          onIntervalFromChange={setIntervalFrom}
          onIntervalToChange={setIntervalTo}
          onMassAssignmentChange={setIsMassAssignmentEnabled}
          onRepeatModeChange={setRepeatMode}
          onSaveIntervals={onSaveIntervals}
          onWeekdayChange={handleWeekdayChange}
        />
      ) : null,
    [
      canAssignIntervalToActivePerformer,
      canAssignIntervals,
      canClearIntervals,
      canSaveIntervals,
      enableIntervalAssignment,
      handleApplyIntervalToActivePerformer,
      handleAssignIntervalsToAll,
      handleClearAllIntervals,
      handleWeekdayChange,
      intervalFrom,
      intervalTo,
      isIntervalAssignmentChanged,
      isManualIntervalAssignment,
      isMassAssignmentEnabled,
      onBackIntervals,
      onCancelIntervals,
      onSaveIntervals,
      repeatMode,
      selectedWeekdays,
    ]
  );
  const renderScheduleAddresses = useCallback(
    () => (
      <ScheduleAddressesSummary
        companyId={companyId}
        schedule={selectedSchedule}
        periodText={selectedSchedulePeriodText}
      />
    ),
    [companyId, selectedSchedule, selectedSchedulePeriodText]
  );

  useEffect(() => {
    setSearchValue('');
    setActivePerformerId(null);
    setActiveIntervalCellKey(null);
    setEditingInterval(null);
  }, [selectedScheduleId]);

  useEffect(() => {
    if (
      intervalAssignmentPristineDraftModes.has(selectedSchedule?._draftMode)
    ) {
      setLastAppliedIntervalAssignmentKey(intervalAssignmentSettingsKey);
      return;
    }

    setLastAppliedIntervalAssignmentKey(null);
  }, [
    intervalAssignmentSettingsKey,
    selectedSchedule._draftMode,
    selectedScheduleId,
  ]);

  useEffect(() => {
    if (isMassAssignmentEnabled || isManualIntervalAssignment) {
      setActivePerformerId(null);
    }
  }, [isManualIntervalAssignment, isMassAssignmentEnabled]);

  useEffect(() => {
    if (isManualIntervalAssignment) {
      setIsMassAssignmentEnabled(false);
      return;
    }

    setActiveIntervalCellKey(null);
    setEditingInterval(null);
  }, [isManualIntervalAssignment]);

  useEffect(() => {
    if (isCompactCalendar && typeof onCompactModeEnable === 'function') {
      onCompactModeEnable();
    }
  }, [isCompactCalendar, onCompactModeEnable]);

  return (
    <ScheduleCalendarView
      enableIntervalAssignment={enableIntervalAssignment}
      filteredPerformers={filteredPerformers}
      handleCompactCalendarToggle={handleCompactCalendarToggle}
      handleSearchChange={handleSearchChange}
      initialSheet={initialSheet}
      isCompactCalendar={isCompactCalendar}
      renderEmptyScheduleTask={renderEmptyScheduleTask}
      renderIntervalAssignment={renderIntervalAssignment}
      renderPerformer={renderPerformer}
      renderScheduleAddresses={renderScheduleAddresses}
      renderScheduleTask={renderScheduleTask}
      schedulesToShow={schedulesToShow}
      searchValue={searchValue}
      selectedScheduleName={selectedScheduleName}
      selectedSchedulePeriodText={selectedSchedulePeriodText}
    />
  );
};
